import React from 'react';
import { LogIn, Settings, Zap, FileDown } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: LogIn,
      step: "01",
      title: "User Login (Cloud-Based)",
      description: "Our quick and hassle-free login system gets you started in no time. Access your GenieFab dashboard from anywhere with your secure cloud account.",
      color: "from-slate-600 to-slate-800"
    },
    {
      icon: Settings,
      step: "02", 
      title: "Input Parameters",
      description: "Enter your design specifications and input parameters directly into the intuitive GenieFab screen. Our smart interface guides you through each requirement.",
      color: "from-slate-700 to-slate-900"
    },
    {
      icon: Zap,
      step: "03",
      title: "Automated Generation",
      description: "Our intelligent system processes your data and rapidly creates the drawings. Watch as complex pressure vessel designs are generated with precision in real-time.",
      color: "from-slate-800 to-black"
    },
    {
      icon: FileDown,
      step: "04",
      title: "Review & Submit",
      description: "Once finished, quickly download the .dwg file. Confidently review the accurate drawings and submit them, knowing they're precise and complete.",
      color: "from-slate-600 to-slate-800"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Simplify Your Workflow: The GenieFab Advantage
            </p>
          </div>

          {/* Intro Text */}
          <div className="text-center mb-16">
            <p className="text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              GenieFab seamlessly integrates into your existing design ecosystem. Our platform intelligently processes 
              your design parameters and specifications, automatically generating detailed and precise static equipment 
              drawings within hours. This eliminates repetitive manual tasks, allowing your team to focus on critical 
              design aspects and innovation, not tedious drafting.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-12">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              const isEven = index % 2 === 1;
              
              return (
                <div 
                  key={index}
                  className={`flex flex-col ${isEven ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}
                >
                  {/* Content */}
                  <div className="flex-1 text-center lg:text-left">
                    <div className="flex items-center justify-center lg:justify-start mb-4">
                      <span className="text-3xl font-bold text-slate-300 mr-4">{step.step}</span>
                      <h3 className="text-2xl font-bold text-slate-900">{step.title}</h3>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                      {step.description}
                    </p>
                  </div>

                  {/* Visual */}
                  <div className="flex-1 flex justify-center">
                    <div className="relative">
                      {/* Main Circle */}
                      <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-xl`}>
                        <IconComponent className="w-12 h-12 text-white" />
                      </div>
                      
                      {/* Step Number Badge */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full border-2 border-slate-200 flex items-center justify-center shadow-sm">
                        <span className="text-xs font-bold text-slate-700">{step.step}</span>
                      </div>
                      
                      {/* Connection Line */}
                      {index < steps.length - 1 && (
                        <div className="hidden lg:block absolute top-1/2 left-full w-24 h-0.5 bg-slate-200 transform -translate-y-1/2 ml-8">
                          <div className="absolute right-0 top-1/2 w-2 h-2 bg-slate-300 rounded-full transform -translate-y-1/2"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                See GenieFab in Action
              </h3>
              <p className="text-slate-600 mb-6">
                Experience the power of automated pressure vessel design. Start your journey today.
              </p>
              <button 
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 mr-4"
                onClick={() => {
                  const element = document.getElementById('pricing');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Get Started Now
              </button>
              <button 
                className="border border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-3 rounded-lg font-medium transition-colors duration-200"
                onClick={() => {
                  const element = document.getElementById('about');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;